import { create } from 'zustand'
import type { Alert } from '../types'
import { getAlerts, approveAlert, rejectAlert, dismissAlert } from '../lib/api'

interface AlertState {
  alerts: Alert[]
  unreadCount: number
  isLoading: boolean
  error: string | null
  fetchAlerts: () => Promise<void>
  approveAction: (alertId: string) => Promise<void>
  rejectAction: (alertId: string) => Promise<void>
  dismissAlert: (alertId: string) => Promise<void>
  getUnreadCount: () => number
}

export const useAlertStore = create<AlertState>()((set, get) => ({
  alerts: [],
  unreadCount: 0,
  isLoading: false,
  error: null,

  fetchAlerts: async () => {
    set({ isLoading: true, error: null })
    try {
      const response = await getAlerts({ status: 'pending', page_size: 100 })
      const pendingCount = response.items.filter((a) => a.status === 'pending').length
      set({
        alerts: response.items,
        unreadCount: pendingCount,
        isLoading: false,
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch alerts'
      set({ error: message, isLoading: false })
    }
  },

  approveAction: async (alertId: string) => {
    try {
      await approveAlert(alertId)
      set((state) => ({
        alerts: state.alerts.map((a) =>
          a.id === alertId ? { ...a, status: 'approved' as const } : a,
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }))
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to approve alert'
      set({ error: message })
    }
  },

  rejectAction: async (alertId: string) => {
    try {
      await rejectAlert(alertId)
      set((state) => ({
        alerts: state.alerts.map((a) =>
          a.id === alertId ? { ...a, status: 'rejected' as const } : a,
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }))
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reject alert'
      set({ error: message })
    }
  },

  dismissAlert: async (alertId: string) => {
    try {
      await dismissAlert(alertId)
      set((state) => ({
        alerts: state.alerts.filter((a) => a.id !== alertId),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }))
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to dismiss alert'
      set({ error: message })
    }
  },

  getUnreadCount: () => {
    return get().alerts.filter((a) => a.status === 'pending').length
  },
}))
